/** @jsxImportSource @emotion/react */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { config } from "../../../Helpers/env";
import { titleCase } from "../../Users/addUser";
import { usePatientInformation } from "../../DoctorsModule/PatientInfo/hooks/usePatientInformation";
import {
    PatientStrip,
    PatientStripAvatar,
    PatientStripName,
    PatientStripDivider,
    PatientStripField,
    PatientStripLabel,
    PatientStripValue,
} from "../Dashboard/ObGynDashboardStyles";

/**
 * ObGynPatientInfo — Compact single-row patient info strip for OB/GYN pages.
 *
 * Replaces the bulky PatientInformation component with a streamlined display:
 * [Avatar] Name + Patient# | Age | Gender | Phone | Payment | Insurance
 *
 * Props:
 *   - patient_number (required)
 *   - visit_id (required)
 *   - isResolved — optional flag passed to usePatientInformation to re-fetch
 *   - children — optional extra content rendered after the strip
 */
const ObGynPatientInfo = ({ patient_number, visit_id, isResolved, children }) => {
    const { patient } = usePatientInformation(patient_number, isResolved);
    const [visitDetails, setVisitDetails] = useState({});

    /* ── Fetch visit details ── */
    useEffect(() => {
        if (!visit_id) return;
        axios
            .get(`${config.smsUrl}/cvisit/${visit_id}`)
            .then((res) => {
                setVisitDetails(res.data || {});
            })
            .catch(() => {});
    }, [visit_id]);

    /* ── Initials for avatar ── */
    const patientName = patient.patient_name || "";
    const initials = patientName
        .split(" ")
        .map((n) => n.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);

    return (
        <div style={{ padding: "12px 16px 0" }}>
            <PatientStrip>
                <PatientStripAvatar>{initials || "?"}</PatientStripAvatar>
                <PatientStripName>
                    {titleCase(patientName) || "Loading..."}
                    <span>Patient No: {patient_number}</span>
                </PatientStripName>
                <PatientStripDivider />
                <PatientStripField>
                    <PatientStripLabel>Age</PatientStripLabel>
                    <PatientStripValue>{patient.age || "—"}</PatientStripValue>
                </PatientStripField>
                <PatientStripField>
                    <PatientStripLabel>Gender</PatientStripLabel>
                    <PatientStripValue>
                        {titleCase(patient.gender) || "—"}
                    </PatientStripValue>
                </PatientStripField>
                <PatientStripField>
                    <PatientStripLabel>Phone</PatientStripLabel>
                    <PatientStripValue>{patient.phone || "—"}</PatientStripValue>
                </PatientStripField>
                <PatientStripDivider />
                <PatientStripField>
                    <PatientStripLabel>Payment</PatientStripLabel>
                    <PatientStripValue>
                        {visitDetails.is_on_insurance === 1
                            ? "Credit"
                            : visitDetails.is_on_insurance === 0
                            ? "Cash"
                            : "—"}
                    </PatientStripValue>
                </PatientStripField>
                {visitDetails.is_on_insurance === 1 && (
                    <PatientStripField>
                        <PatientStripLabel>Insurance</PatientStripLabel>
                        <PatientStripValue>
                            {visitDetails.insurance_provider_name || "—"}
                        </PatientStripValue>
                    </PatientStripField>
                )}
            </PatientStrip>
            {children}
        </div>
    );
};

export default ObGynPatientInfo;
